import { useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, X } from "lucide-react";
import {
  cancelAiLogin,
  enableAiProvider,
  fetchAiLogin,
  fetchAiProviders,
  removeAiProvider,
  startAiLogin,
  type AiProviders,
} from "../api";
import { AiProviderIcon } from "./AiProviderIcon";

type LoginDialog = {
  provider: string;
  name: string;
  taskId: string;
  qr: string;
  webUrl: string;
  error: string;
  loading: boolean;
};
export function AiSettings() {
  const [data, setData] = useState<AiProviders | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [dialog, setDialog] = useState<LoginDialog | null>(null);
  const activeTask = useRef("");
  const load = () =>
    fetchAiProviders()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : "加载失败"))
      .finally(() => setLoading(false));
  useEffect(() => {
    void load();
    return () => {
      const taskId = activeTask.current;
      activeTask.current = "";
      if (taskId) void cancelAiLogin(taskId).catch(() => undefined);
    };
  }, []);
  const closeDialog = () => {
    const taskId = activeTask.current;
    activeTask.current = "";
    setDialog(null);
    if (taskId) void cancelAiLogin(taskId).catch(() => undefined);
  };
  const login = async (provider: string, name: string) => {
    setError("");
    setDialog({ provider, name, taskId: "", qr: "", webUrl: "", error: "", loading: true });
    try {
      const task = await startAiLogin(provider);
      if (!task?.id) throw new Error("AI 来源没有返回有效的登录任务");
      activeTask.current = task.id;
      setDialog((current) => current ? { ...current, taskId: task.id,webUrl:String(task.web_url||""),loading:!task.web_url } : null);
      for (let i = 0; i < 300; i++) {
        if (activeTask.current !== task.id) return;
        const raw: unknown = i ? await fetchAiLogin(task.id) : task;
        const state: Record<string, unknown> =
          raw && typeof raw === "object"
            ? (raw as Record<string, unknown>)
            : {};
        const image = String(state.qr_data || state.qr_url || "");
        if (image)
          setDialog((current) =>
            current
              ? {
                  ...current,
                  qr: state.qr_data
                    ? `data:${state.qr_mime || "image/png"};base64,${image}`
                    : image,
                  loading: false,
                }
              : null,
          );
        if (state.status === "success") {
          activeTask.current = "";
          setDialog(null);
          await load();
          return;
        }
        if (["error", "expired", "canceled"].includes(String(state.status)))
          throw new Error(String(state.error || "登录失败"));
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      throw new Error("等待登录超时");
    } catch (cause) {
      const taskId=activeTask.current;
      activeTask.current="";
      if(taskId)void cancelAiLogin(taskId).catch(()=>undefined);
      let message = cause instanceof Error ? cause.message : "登录失败";
      if (provider === "yuanbao" && message.includes("签名"))
        message = `腾讯元宝安全服务初始化失败：${message}`;
      setDialog((current) =>
        current ? { ...current, error: message, loading: false } : null,
      );
    }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold">AI 设置</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            绑定你自己的 AI 账户，登录凭据加密保存到数据库。 Lua 内核 {data?.runtime?.version ? `v${data.runtime.version}` : "版本未知"}。
          </p>
        </div>
        <button
          onClick={() => {
            setLoading(true);
            void load();
          }}
          className="w-10 h-10 flex items-center justify-center"
          aria-label="刷新"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {error && (
        <p className="mb-4 p-3 border border-rose text-sm text-rose rounded-lg">
          {error}
        </p>
      )}
      {loading ? (
        <div className="py-16 flex justify-center">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          {(data?.providers || []).map((provider) => (
            <div
              key={provider.key}
              className="flex items-center gap-4 p-4 border border-[var(--border-default)] rounded-lg"
            >
              <AiProviderIcon provider={provider.key} name={provider.name} className="w-10 h-10" />
              <div className="flex-1">
                <strong>{provider.name || provider.key}</strong>
                <p className="text-sm text-[var(--text-secondary)]">
                  {provider.logged_in
                    ? `已登录${provider.account_label ? `：${provider.account_label}` : ""}`
                    : "未登录"}
                </p>
              </div>
              {provider.logged_in ? (
                <>
                  <button
                    onClick={() =>
                      void enableAiProvider(provider.key).then(load)
                    }
                    className="h-10 px-4 border border-[var(--border-default)] rounded-lg"
                  >
                    {data?.enabled_provider === provider.key
                      ? "已启用"
                      : "启用"}
                  </button>
                  <button
                    onClick={() =>
                      void removeAiProvider(provider.key).then(load)
                    }
                    className="h-10 px-4 text-rose"
                  >
                    移除
                  </button>
                </>
              ) : (
                <button
                  onClick={() =>
                    void login(provider.key, provider.name || provider.key)
                  }
                  className="h-10 px-4 bg-primary text-white rounded-lg"
                >
                  登录
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {dialog && (
        <div
          className="fixed inset-0 z-[100] bg-black/55 flex items-center justify-center p-5"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-bold">登录 {dialog.name}</h3>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  {dialog.webUrl ? "可使用手机号验证码或扫码登录" : "正在准备登录"}
                </p>
              </div>
              <button
                onClick={closeDialog}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[var(--bg-hover)]"
                aria-label="关闭"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {dialog.loading && (
              <div className="h-56 flex flex-col items-center justify-center gap-3">
                <Loader2 className="animate-spin" />
                <p className="text-sm text-[var(--text-secondary)]">
                  正在获取二维码…
                </p>
              </div>
            )}
            {dialog.webUrl && <iframe src={dialog.webUrl} title={`登录 ${dialog.name}`} className="w-full h-[560px] border-0 rounded-lg" />}
            {dialog.qr && (
              <>
                <img
                  src={dialog.qr}
                  alt={`${dialog.name} 登录二维码`}
                  className="w-56 h-56 object-contain mx-auto bg-white p-2"
                />
                <p className="text-sm text-center mt-4">
                  扫码成功后弹窗会自动关闭
                </p>
              </>
            )}
            {dialog.error && (
              <div className="text-sm leading-6 text-rose border border-rose/30 p-4 rounded-lg">
                {dialog.error}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
